/**
 * Executive Management Questions - Board Level Premium Component
 * "As 3 Questões Críticas do Trimestre" - Bloco executivo para CFO / Management
 * 
 * MOTOR QUANTITATIVO:
 * - Avaliação automática baseada em KPIs vs Orçamento
 * - Scoring: Verde=100, Âmbar=65, Vermelho=30
 * - Média ponderada por card
 * - Override rules para situações críticas
 * - Mesmo motor para 2025 e 2026 (apenas texto interpretativo diferente)
 */

import React, { useState, useMemo } from 'react';
import { 
  ShieldCheck, ChevronDown, ChevronUp, Gem, Gauge, Target, DollarSign
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// ============ DESIGN TOKENS ============
const colors = {
  status: {
    // Card 1 - Base Física da Receita
    protegida: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
    parcialmenteProtegida: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    sobPressaoReceita: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' },
    fragil: { bg: '#FEE2E2', text: '#7F1D1D', border: '#FCA5A5' },
    // Card 2 - Drivers de Custo
    sobControlo: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
    comAtencao: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    sobPressaoCusto: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' },
    foraDeControlo: { bg: '#FEE2E2', text: '#7F1D1D', border: '#FCA5A5' },
    // Card 3 - Robustez da Execução
    robusta: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
    parcial: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    aindaFragil: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' },
    emRisco: { bg: '#FEE2E2', text: '#7F1D1D', border: '#FCA5A5' },
  },
  kpi: {
    green: { bg: '#DCFCE7', text: '#166534' },
    amber: { bg: '#FEF3C7', text: '#92400E' },
    red: { bg: '#FEE2E2', text: '#991B1B' },
  },
  bg: {
    card: '#FFFFFF',
    dark: '#0A192F',
    gold: '#C5A059',
    goldLight: '#FBF8F0',
  },
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#64748B',
    inverse: '#FFFFFF',
  }
};

// ============ SCORING CONSTANTS ============
const SCORES = { GREEN: 100, AMBER: 65, RED: 30 };

// ============ KPI RAW DATA ============
const kpiData = {
  2026: {
    // Card 1 - Base Física da Receita
    quilatesRecuperados: { actual: 42.5, budget: 45.0, unit: 'Kcts' },
    teorAlimentacao: { actual: 15.74, budget: 16.10, unit: 'cpht' },
    tamanhoMedioPedra: { actual: 0.39, budget: 0.44, unit: 'ct/pedra' },
    // Card 2 - Drivers de Custo
    volumeNaoProdutivo: { actual: 58000, budget: 45000, unit: 'm³' },
    horasParagemPlanta: { actual: 142, budget: 100, unit: 'hrs' },
    stripRatio: { actual: 1.94, budget: 2.10, unit: ':1' },
    // Card 3 - Robustez da Execução
    gapQuilates: { actual: -2.4, budget: 0, budgetTotal: 45.0, unit: 'Kcts' }, // gap em valor absoluto
    dependenciaStockpile: { actual: 35.2, budget: 28.0, unit: '%' }, // percentagem
    disponibilidadePlanta: { actual: 92.3, budget: 95.0, unit: '%' },
  },
  2025: {
    // Card 1 - Base Física da Receita
    quilatesRecuperados: { actual: 168.2, budget: 172.8, unit: 'Kcts' },
    teorAlimentacao: { actual: 16.02, budget: 15.80, unit: 'cpht' },
    tamanhoMedioPedra: { actual: 0.42, budget: 0.45, unit: 'ct/pedra' },
    // Card 2 - Drivers de Custo
    volumeNaoProdutivo: { actual: 245000, budget: 180000, unit: 'm³' },
    horasParagemPlanta: { actual: 485, budget: 400, unit: 'hrs' },
    stripRatio: { actual: 1.95, budget: 2.10, unit: ':1' },
    // Card 3 - Robustez da Execução
    gapQuilates: { actual: -4.7, budget: 0, budgetTotal: 172.8, unit: 'Kcts' },
    dependenciaStockpile: { actual: 38.5, budget: 30.0, unit: '%' },
    disponibilidadePlanta: { actual: 93.8, budget: 94.5, unit: '%' },
  }
};

// ============ THRESHOLD FUNCTIONS ============

// Card 1 KPIs (more is better)
const evaluateQuilates = (actual, budget) => {
  const ratio = (actual / budget) * 100;
  if (ratio >= 95) return 'GREEN';
  if (ratio >= 90) return 'AMBER';
  return 'RED';
};

const evaluateTeor = (actual, budget) => {
  const ratio = (actual / budget) * 100;
  if (ratio >= 97) return 'GREEN';
  if (ratio >= 92) return 'AMBER';
  return 'RED';
};

const evaluateTamanhoPedra = (actual, budget) => {
  const ratio = (actual / budget) * 100;
  if (ratio >= 95) return 'GREEN';
  if (ratio >= 90) return 'AMBER';
  return 'RED';
};

// Card 2 KPIs (less is better for volume/hours, band for strip ratio)
const evaluateVolumeNaoProdutivo = (actual, budget) => {
  const ratio = (actual / budget) * 100;
  if (ratio <= 105) return 'GREEN';
  if (ratio <= 115) return 'AMBER';
  return 'RED';
};

const evaluateHorasParagem = (actual, budget) => {
  const ratio = (actual / budget) * 100;
  if (ratio <= 105) return 'GREEN';
  if (ratio <= 115) return 'AMBER';
  return 'RED';
};

const evaluateStripRatio = (actual, budget) => {
  const desvio = Math.abs(((actual - budget) / budget) * 100);
  if (desvio <= 5) return 'GREEN';
  if (desvio <= 10) return 'AMBER';
  return 'RED';
};

// Card 3 KPIs
const evaluateGapQuilates = (actual, budgetTotal) => {
  const gapPercent = Math.abs((actual / budgetTotal) * 100);
  if (gapPercent <= 3) return 'GREEN';
  if (gapPercent <= 8) return 'AMBER';
  return 'RED';
};

const evaluateDependenciaStockpile = (actual, budget) => {
  const diff = actual - budget; // pontos percentuais acima do plano
  if (diff <= 3) return 'GREEN';
  if (diff <= 8) return 'AMBER';
  return 'RED';
};

const evaluateDisponibilidade = (actual, budget) => {
  const diff = budget - actual; // pontos percentuais abaixo do plano
  if (diff <= 2) return 'GREEN';
  if (diff <= 5) return 'AMBER';
  return 'RED';
};

// ============ CARD SCORING ENGINE ============

const calculateCard1Score = (data) => {
  const kpis = [
    { eval: evaluateQuilates(data.quilatesRecuperados.actual, data.quilatesRecuperados.budget), weight: 0.45 },
    { eval: evaluateTeor(data.teorAlimentacao.actual, data.teorAlimentacao.budget), weight: 0.30 },
    { eval: evaluateTamanhoPedra(data.tamanhoMedioPedra.actual, data.tamanhoMedioPedra.budget), weight: 0.25 },
  ];
  
  let totalScore = 0;
  let redCount = 0;
  
  kpis.forEach(kpi => {
    totalScore += SCORES[kpi.eval] * kpi.weight;
    if (kpi.eval === 'RED') redCount++;
  });
  
  // Override: Se 2+ KPIs em vermelho, máximo é "Sob pressão"
  const hasOverride = redCount >= 2;
  
  return { 
    score: totalScore, 
    hasOverride,
    kpiResults: kpis.map((k, i) => ({
      ...k,
      kpiName: ['quilatesRecuperados', 'teorAlimentacao', 'tamanhoMedioPedra'][i]
    }))
  };
};

const calculateCard2Score = (data) => {
  const kpis = [
    { eval: evaluateVolumeNaoProdutivo(data.volumeNaoProdutivo.actual, data.volumeNaoProdutivo.budget), weight: 0.40 },
    { eval: evaluateHorasParagem(data.horasParagemPlanta.actual, data.horasParagemPlanta.budget), weight: 0.35 },
    { eval: evaluateStripRatio(data.stripRatio.actual, data.stripRatio.budget), weight: 0.25 },
  ];
  
  let totalScore = 0;
  let redCount = 0;
  
  kpis.forEach(kpi => {
    totalScore += SCORES[kpi.eval] * kpi.weight;
    if (kpi.eval === 'RED') redCount++;
  });
  
  const hasOverride = redCount >= 2;
  
  return { 
    score: totalScore, 
    hasOverride,
    kpiResults: kpis.map((k, i) => ({
      ...k,
      kpiName: ['volumeNaoProdutivo', 'horasParagemPlanta', 'stripRatio'][i]
    }))
  };
};

const calculateCard3Score = (data) => {
  const kpis = [
    { eval: evaluateGapQuilates(data.gapQuilates.actual, data.gapQuilates.budgetTotal), weight: 0.40 },
    { eval: evaluateDependenciaStockpile(data.dependenciaStockpile.actual, data.dependenciaStockpile.budget), weight: 0.35 },
    { eval: evaluateDisponibilidade(data.disponibilidadePlanta.actual, data.disponibilidadePlanta.budget), weight: 0.25 },
  ];
  
  let totalScore = 0;
  let redCount = 0;
  
  kpis.forEach(kpi => {
    totalScore += SCORES[kpi.eval] * kpi.weight;
    if (kpi.eval === 'RED') redCount++;
  });
  
  const hasOverride = redCount >= 2;
  
  return { 
    score: totalScore, 
    hasOverride,
    kpiResults: kpis.map((k, i) => ({
      ...k,
      kpiName: ['gapQuilates', 'dependenciaStockpile', 'disponibilidadePlanta'][i]
    }))
  };
};

// ============ STATUS DETERMINATION ============

const getCard1Status = (score, hasOverride) => {
  if (hasOverride && score >= 55) return 'sobPressaoReceita';
  if (score >= 85) return 'protegida';
  if (score >= 70) return 'parcialmenteProtegida';
  if (score >= 55) return 'sobPressaoReceita';
  return 'fragil';
};

const getCard2Status = (score, hasOverride) => {
  if (hasOverride && score >= 55) return 'sobPressaoCusto';
  if (score >= 85) return 'sobControlo';
  if (score >= 70) return 'comAtencao';
  if (score >= 55) return 'sobPressaoCusto';
  return 'foraDeControlo';
};

const getCard3Status = (score, hasOverride) => {
  if (hasOverride && score >= 55) return 'aindaFragil';
  if (score >= 85) return 'robusta';
  if (score >= 70) return 'parcial';
  if (score >= 55) return 'aindaFragil';
  return 'emRisco';
};

// ============ STATUS LABELS ============
const statusLabels = {
  // Card 1
  protegida: { pt: 'Protegida', en: 'Protected' },
  parcialmenteProtegida: { pt: 'Parcialmente protegida', en: 'Partially protected' },
  sobPressaoReceita: { pt: 'Sob pressão', en: 'Under pressure' },
  fragil: { pt: 'Frágil', en: 'Fragile' },
  // Card 2
  sobControlo: { pt: 'Sob controlo', en: 'Under control' },
  comAtencao: { pt: 'Com atenção', en: 'With attention' },
  sobPressaoCusto: { pt: 'Sob pressão', en: 'Under pressure' },
  foraDeControlo: { pt: 'Fora de controlo', en: 'Out of control' },
  // Card 3
  robusta: { pt: 'Robusta', en: 'Robust' },
  parcial: { pt: 'Parcial', en: 'Partial' },
  aindaFragil: { pt: 'Ainda frágil', en: 'Still fragile' },
  emRisco: { pt: 'Em risco', en: 'At risk' },
};

// ============ KPI LABELS ============
const kpiLabels = {
  quilatesRecuperados: { pt: 'Quilates recuperados', en: 'Carats recovered' },
  teorAlimentacao: { pt: 'Teor de alimentação', en: 'Feed grade' },
  tamanhoMedioPedra: { pt: 'Tamanho médio da pedra', en: 'Avg stone size' },
  volumeNaoProdutivo: { pt: 'Volume não produtivo', en: 'Non-productive volume' },
  horasParagemPlanta: { pt: 'Horas paragem planta', en: 'Plant downtime' },
  stripRatio: { pt: 'Strip ratio', en: 'Strip ratio' },
  gapQuilates: { pt: 'Gap quilates vs orçamento', en: 'Carats gap vs budget' },
  dependenciaStockpile: { pt: 'Dependência de stockpile', en: 'Stockpile dependency' },
  disponibilidadePlanta: { pt: 'Disponibilidade planta', en: 'Plant availability' },
};

// ============ MANUAL INTERPRETATIONS (2026 - Current execution) ============
const interpretations2026 = {
  card1: {
    leituraExecutiva: {
      pt: 'Volume próximo do plano, mas qualidade do produto abaixo das expectativas. O tamanho médio da pedra pressiona o potencial de monetização.',
      en: 'Volume close to plan, but product quality below expectations. Average stone size is pressuring monetization potential.'
    },
    implicacaoFinanceira: {
      pt: 'Pressão moderada sobre receita e mix comercial. Necessidade de recuperação no 2T.',
      en: 'Moderate pressure on revenue and commercial mix. Q2 recovery required.'
    }
  },
  card2: {
    leituraExecutiva: {
      pt: 'Strip ratio com boa disciplina. Paragens da planta e volume não produtivo pressionam eficiência global.',
      en: 'Strip ratio with good discipline. Plant shutdowns and non-productive volume pressure overall efficiency.'
    },
    implicacaoFinanceira: {
      pt: 'Pressão sobre cash cost. Risco de deterioração do custo unitário se paragens persistirem.',
      en: 'Cash cost pressure. Unit cost deterioration risk if shutdowns persist.'
    }
  },
  card3: {
    leituraExecutiva: {
      pt: 'Execução suportada pelo stockpile e por condições que exigem acompanhamento. Estabilidade da planta é o principal ponto de atenção.',
      en: 'Execution supported by stockpile and conditions requiring monitoring. Plant stability is the main attention point.'
    },
    implicacaoFinanceira: {
      pt: 'Risco acrescido sobre previsibilidade do 2T. Maior vulnerabilidade do plano financeiro nos próximos períodos.',
      en: 'Increased Q2 predictability risk. Greater financial plan vulnerability in coming periods.'
    }
  }
};

// ============ MANUAL INTERPRETATIONS (2025 - Retrospective) ============
const interpretations2025 = {
  card1: {
    leituraExecutiva: {
      pt: 'O ano evidenciou volume próximo do plano com teor acima das expectativas. O mix de produto foi ligeiramente pressionado pelo tamanho médio da pedra.',
      en: 'The year showed volume close to plan with grade above expectations. Product mix was slightly pressured by average stone size.'
    },
    implicacaoFinanceira: {
      pt: 'Impacto limitado sobre receita. A base física da receita ficou protegida dentro da margem de tolerância.',
      en: 'Limited revenue impact. Physical revenue base remained protected within tolerance margin.'
    }
  },
  card2: {
    leituraExecutiva: {
      pt: 'A operação de 2025 revelou boa disciplina no strip ratio. O volume não produtivo foi o principal ponto de pressão sobre custos.',
      en: '2025 operations revealed good strip ratio discipline. Non-productive volume was the main cost pressure point.'
    },
    implicacaoFinanceira: {
      pt: 'O ano terminou com pressão moderada sobre cash cost. Volume não produtivo a monitorizar em 2026.',
      en: 'Year ended with moderate cash cost pressure. Non-productive volume to monitor in 2026.'
    }
  },
  card3: {
    leituraExecutiva: {
      pt: 'O plano foi entregue com dependência relevante de stockpile. Ficou demonstrado que a planta operou com disponibilidade elevada.',
      en: 'Plan was delivered with relevant stockpile dependency. Plant was demonstrated to operate with high availability.'
    },
    implicacaoFinanceira: {
      pt: 'A execução de 2025 reduziu o risco para 2026. Base sólida de aprendizagens operacionais.',
      en: '2025 execution reduced risk for 2026. Solid base of operational learnings.'
    }
  }
};

// ============ FORMAT FUNCTIONS ============
const formatValue = (value, unit) => {
  if (unit === 'Kcts') return `${value.toFixed(1)} Kcts`;
  if (unit === 'cpht') return `${value.toFixed(2)} cpht`;
  if (unit === 'ct/pedra') return `${value.toFixed(2)} ct`;
  if (unit === 'm³') return value >= 1000 ? `${(value/1000).toFixed(0)}K m³` : `${value} m³`;
  if (unit === 'hrs') return `${value} hrs`;
  if (unit === ':1') return `${value.toFixed(2)}:1`;
  if (unit === '%') return `${value.toFixed(1)}%`;
  return value.toString();
};

const formatVariance = (actual, budget, type = 'percent') => {
  if (type === 'percent') {
    const variance = ((actual - budget) / budget) * 100;
    return `${variance >= 0 ? '+' : ''}${variance.toFixed(1)}%`;
  }
  if (type === 'pp') {
    const diff = actual - budget;
    return `${diff >= 0 ? '+' : ''}${diff.toFixed(1)} p.p.`;
  }
  return '';
};

// ============ STATUS BADGE COMPONENT ============
const StatusBadge = ({ status, language }) => {
  const statusConfig = colors.status[status] || colors.status.parcialmenteProtegida;
  const label = statusLabels[status]?.[language] || status;
  
  return (
    <span
      className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
      style={{
        backgroundColor: statusConfig.bg,
        color: statusConfig.text,
        border: `1px solid ${statusConfig.border}`
      }}
    >
      {label}
    </span>
  );
};

// ============ KPI STATUS INDICATOR ============
const KPIStatusDot = ({ evalResult }) => {
  const colorMap = {
    GREEN: colors.kpi.green,
    AMBER: colors.kpi.amber,
    RED: colors.kpi.red,
  };
  const c = colorMap[evalResult] || colorMap.AMBER;
  
  return (
    <div 
      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: c.text }}
      title={evalResult}
    />
  );
};

// ============ EVIDENCE TABLE COMPONENT ============
const EvidenceTable = ({ kpiResults, rawData, language }) => {
  const getVarianceColor = (evalResult) => {
    if (evalResult === 'GREEN') return colors.kpi.green;
    if (evalResult === 'AMBER') return colors.kpi.amber;
    return colors.kpi.red;
  };

  const formatKPIVariance = (kpiName, data) => {
    if (kpiName === 'dependenciaStockpile' || kpiName === 'disponibilidadePlanta') {
      return formatVariance(data.actual, data.budget, 'pp');
    }
    if (kpiName === 'gapQuilates') {
      const pct = (data.actual / data.budgetTotal) * 100;
      return `${pct.toFixed(1)}%`;
    }
    return formatVariance(data.actual, data.budget);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <div 
        className="grid grid-cols-12 gap-2 px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-slate-500"
        style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}
      >
        <div className="col-span-5">{language === 'pt' ? 'Indicador' : 'Indicator'}</div>
        <div className="col-span-3 text-right">{language === 'pt' ? 'Valor' : 'Value'}</div>
        <div className="col-span-4 text-right">{language === 'pt' ? 'vs Orç.' : 'vs Budget'}</div>
      </div>
      
      <div className="bg-white">
        {kpiResults.map((kpi, idx) => {
          const data = rawData[kpi.kpiName];
          const varColor = getVarianceColor(kpi.eval);
          
          return (
            <div 
              key={kpi.kpiName}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center"
              style={{ borderBottom: idx < kpiResults.length - 1 ? '1px solid #F1F5F9' : 'none' }}
            >
              <div className="col-span-5 flex items-center gap-2">
                <KPIStatusDot evalResult={kpi.eval} />
                <span className="text-[11px] text-slate-600 font-medium">
                  {kpiLabels[kpi.kpiName]?.[language] || kpi.kpiName}
                </span>
              </div>
              <div className="col-span-3 text-right">
                <span className="text-[12px] font-bold text-slate-800">
                  {formatValue(data.actual, data.unit)}
                </span>
              </div>
              <div className="col-span-4 text-right">
                <span 
                  className="inline-flex items-center text-[10px] font-bold px-2 py-1 rounded"
                  style={{ color: varColor.text, backgroundColor: varColor.bg }}
                >
                  {formatKPIVariance(kpi.kpiName, data)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============ SCORE DEBUG DISPLAY (optional, for validation) ============
const ScoreDebug = ({ score, hasOverride }) => {
  return (
    <div className="text-[8px] text-slate-400 mt-1 flex items-center gap-2">
      <span>Score: {score.toFixed(1)}</span>
      {hasOverride && <span className="text-amber-500">(override)</span>}
    </div>
  );
};

// ============ QUESTION CARD COMPONENT ============
const QuestionCard = ({ 
  cardNumber, 
  question, 
  icon: Icon, 
  status, 
  score,
  hasOverride,
  kpiResults, 
  rawData,
  leituraExecutiva, 
  implicacaoFinanceira, 
  language, 
  isExpanded, 
  onToggle,
  showDebug = false
}) => {
  const romanNumerals = { 1: 'I', 2: 'II', 3: 'III' };
  
  return (
    <div 
      className="bg-white rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden transition-all duration-300 hover:shadow-xl"
      data-testid={`management-question-${cardNumber}`}
    >
      {/* Card Header */}
      <div 
        className="px-6 py-6 cursor-pointer"
        onClick={onToggle}
        style={{ 
          background: `linear-gradient(135deg, ${colors.bg.goldLight} 0%, #FFFFFF 100%)`,
          borderBottom: `1px solid ${colors.bg.gold}20`
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.bg.dark }}
            >
              <span className="text-[11px] font-bold" style={{ color: colors.bg.gold }}>
                {romanNumerals[cardNumber]}
              </span>
            </div>
            <StatusBadge status={status} language={language} />
          </div>
          <button className="p-1.5 rounded-full hover:bg-slate-200/50 transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
        </div>
        
        <h3 
          className="text-[17px] font-semibold leading-relaxed pr-4"
          style={{ color: colors.text.primary }}
        >
          {question[language]}
        </h3>
        
        {showDebug && <ScoreDebug score={score} hasOverride={hasOverride} />}
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 py-5 space-y-5">
          {/* Evidência Principal */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: colors.bg.gold }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {language === 'pt' ? 'Evidência Principal' : 'Key Evidence'}
              </span>
            </div>
            <EvidenceTable kpiResults={kpiResults} rawData={rawData} language={language} />
          </div>
          
          {/* Leitura Executiva */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: colors.bg.gold }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {language === 'pt' ? 'Leitura Executiva' : 'Executive Reading'}
              </span>
            </div>
            <p className="text-[13px] text-slate-700 leading-relaxed pl-4">
              {leituraExecutiva[language]}
            </p>
          </div>
          
          {/* Implicação Financeira */}
          <div 
            className="rounded-xl p-4"
            style={{ 
              backgroundColor: colors.bg.dark,
              boxShadow: '0 4px 12px rgba(10, 25, 47, 0.15)'
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4" style={{ color: colors.bg.gold }} />
              <span 
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: colors.bg.gold }}
              >
                {language === 'pt' ? 'Implicação Financeira' : 'Financial Implication'}
              </span>
            </div>
            <p className="text-[13px] leading-relaxed text-white/90">
              {implicacaoFinanceira[language]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ MAIN COMPONENT ============
const ExecutiveManagementQuestions = ({ year = 2026, showDebugScores = false }) => {
  const { language } = useLanguage();
  const [expandedCards, setExpandedCards] = useState({ 1: true, 2: true, 3: true });
  
  const periodLabel = year === 2026 ? '1º Trimestre 2026' : '2025';
  const rawData = kpiData[year] || kpiData[2026];
  const interpretations = year === 2025 ? interpretations2025 : interpretations2026;
  
  // Calculate scores using the quantitative engine
  const cardScores = useMemo(() => {
    const card1 = calculateCard1Score(rawData);
    const card2 = calculateCard2Score(rawData);
    const card3 = calculateCard3Score(rawData);
    
    return {
      card1: {
        ...card1,
        status: getCard1Status(card1.score, card1.hasOverride)
      },
      card2: {
        ...card2,
        status: getCard2Status(card2.score, card2.hasOverride)
      },
      card3: {
        ...card3,
        status: getCard3Status(card3.score, card3.hasOverride)
      }
    };
  }, [rawData]);
  
  const toggleCard = (cardNumber) => {
    setExpandedCards(prev => ({ ...prev, [cardNumber]: !prev[cardNumber] }));
  };
  
  const expandAll = () => setExpandedCards({ 1: true, 2: true, 3: true });
  const collapseAll = () => setExpandedCards({ 1: false, 2: false, 3: false });

  const questions = {
    q1: {
      pt: 'A operação está a proteger a base física da receita?',
      en: 'Is the operation protecting the physical revenue base?'
    },
    q2: {
      pt: 'Os principais drivers operacionais de custo estão sob controlo?',
      en: 'Are the main operational cost drivers under control?'
    },
    q3: {
      pt: year === 2026 
        ? 'O plano de 2026 está a ser executado com robustez?'
        : 'O plano de 2025 foi executado com robustez?',
      en: year === 2026
        ? 'Is the 2026 plan being executed robustly?'
        : 'Was the 2025 plan executed robustly?'
    }
  };

  return (
    <div className="space-y-5" data-testid="executive-management-questions">
      {/* Section Header */}
      <div className="flex items-end justify-between border-b border-slate-200 pb-4">
        <div>
          <h2 
            className="font-serif text-2xl font-bold tracking-tight flex items-center gap-3"
            style={{ color: colors.text.primary }}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
              style={{ backgroundColor: colors.bg.dark }}
            >
              <ShieldCheck className="w-5 h-5" style={{ color: colors.bg.gold }} />
            </div>
            {language === 'pt' 
              ? `As 3 Questões Críticas Operacionais ${year === 2026 ? 'do' : 'de'} ${periodLabel}`
              : `The 3 Critical Operational Questions for ${periodLabel}`
            }
          </h2>
          <p 
            className="text-[13px] mt-2 ml-[52px]"
            style={{ color: colors.text.secondary }}
          >
            {language === 'pt' 
              ? 'O que mais importa para receita, custo e robustez do plano'
              : 'What matters most for revenue, cost and plan robustness'
            }
          </p>
        </div>
        
        <div className="flex items-center gap-2 mb-1">
          <button
            onClick={expandAll}
            className="text-[10px] font-semibold text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
          >
            {language === 'pt' ? 'Expandir' : 'Expand'}
          </button>
          <button
            onClick={collapseAll}
            className="text-[10px] font-semibold text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
          >
            {language === 'pt' ? 'Colapsar' : 'Collapse'}
          </button>
        </div>
      </div>
      
      {/* Questions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <QuestionCard 
          cardNumber={1}
          question={questions.q1}
          icon={Gem}
          status={cardScores.card1.status}
          score={cardScores.card1.score}
          hasOverride={cardScores.card1.hasOverride}
          kpiResults={cardScores.card1.kpiResults}
          rawData={rawData}
          leituraExecutiva={interpretations.card1.leituraExecutiva}
          implicacaoFinanceira={interpretations.card1.implicacaoFinanceira}
          language={language}
          isExpanded={expandedCards[1]}
          onToggle={() => toggleCard(1)}
          showDebug={showDebugScores}
        />
        <QuestionCard 
          cardNumber={2}
          question={questions.q2}
          icon={Gauge}
          status={cardScores.card2.status}
          score={cardScores.card2.score}
          hasOverride={cardScores.card2.hasOverride}
          kpiResults={cardScores.card2.kpiResults}
          rawData={rawData}
          leituraExecutiva={interpretations.card2.leituraExecutiva}
          implicacaoFinanceira={interpretations.card2.implicacaoFinanceira}
          language={language}
          isExpanded={expandedCards[2]}
          onToggle={() => toggleCard(2)}
          showDebug={showDebugScores}
        />
        <QuestionCard 
          cardNumber={3}
          question={questions.q3}
          icon={Target}
          status={cardScores.card3.status}
          score={cardScores.card3.score}
          hasOverride={cardScores.card3.hasOverride}
          kpiResults={cardScores.card3.kpiResults}
          rawData={rawData}
          leituraExecutiva={interpretations.card3.leituraExecutiva}
          implicacaoFinanceira={interpretations.card3.implicacaoFinanceira}
          language={language}
          isExpanded={expandedCards[3]}
          onToggle={() => toggleCard(3)}
          showDebug={showDebugScores}
        />
      </div>
    </div>
  );
};

export default ExecutiveManagementQuestions;
